// ============================================================
// THE HEADS - each class's plain polyhedron (plan map-display, Track B).
//
// The user's ruling (2026-10-07): NO turrets, spikes or face marks - the
// head's shape alone says what it is. A tank or a skimmer: a cube. A walker:
// a hexagonal prism (held high - the caller places it). A bike: a small, low
// cube. The living are a sphere and the amorphous a blob, drawn as outlines:
// they have no faces here.
//
// Faces are in WORLD space, turned with the heading, each with its outward
// normal so the renderer can drop the ones facing away.
//
// Pure. Zero React dependency.
// ============================================================
import type { Vec3 } from '../battlefield/types';
import { project, type Camera, type ScreenPoint, type Viewport } from '../battlefield/projection';
import { isFrontFacing, sightTo } from '../battlefield/scene';
import type { FigureClass } from './figureModel';
import { closedPath } from './svgPath';

type HeadFaceKind = 'top' | 'bottom' | 'front' | 'back' | 'side';

export interface HeadFace {
  kind: HeadFaceKind;
  normal: Vec3;
  points: Vec3[];
}

type Local = [number, number, number];

type LocalFace = { kind: HeadFaceKind; n: Local; p: Local[] };

/**
 * A right prism of `n` sides around the vertical axis: circumradius `r`,
 * half-height `h`, two caps and `n` sides. A flat side faces the heading (the
 * `front` face) and, for an even `n`, its opposite is the `back`; four sides
 * make a box.
 */
function prism(n: number, r: number, h: number): LocalFace[] {
  const ang = (i: number) => -Math.PI / n + (i * 2 * Math.PI) / n;
  const v = (i: number, y: number): Local => [r * Math.cos(ang(i)), y, r * Math.sin(ang(i))];
  const ring = (y: number, reverse: boolean) => Array.from({ length: n }, (_, i) => v(reverse ? n - 1 - i : i, y));
  const kindOf = (i: number): HeadFaceKind => (i === 0 ? 'front' : i === n / 2 ? 'back' : 'side');
  const sides = Array.from({ length: n }, (_, i): LocalFace => {
    const m = ang(i) + Math.PI / n;
    return { kind: kindOf(i), n: [Math.cos(m), 0, Math.sin(m)], p: [v(i, -h), v(i, h), v(i + 1, h), v(i + 1, -h)] };
  });
  return [
    { kind: 'top', n: [0, 1, 0], p: ring(h, false) },
    { kind: 'bottom', n: [0, -1, 0], p: ring(-h, true) },
    ...sides,
  ];
}

/** An Object's diamond (2026-10-26, board 5): its half-height and its waist's radius, as shares of the head's size. */
export const OBJECT_HALF_HEIGHT = 0.8;
const OBJECT_WAIST = 0.55;

/**
 * An octahedron: four triangles up to the top tip, four down to the bottom one, round a square waist turned 45° so
 * a corner (not a face) leads. Convex and centred, so each face's outward normal is its centroid's direction.
 */
function octahedron(s: number): LocalFace[] {
  const h = s * OBJECT_HALF_HEIGHT;
  const waist = Array.from({ length: 4 }, (_, i): Local => {
    const a = Math.PI / 4 + (i * Math.PI) / 2;
    return [s * OBJECT_WAIST * Math.cos(a), 0, s * OBJECT_WAIST * Math.sin(a)];
  });
  const face = (kind: HeadFaceKind, p: Local[]): LocalFace => {
    const c = p.reduce<Local>((m, q) => [m[0] + q[0] / 3, m[1] + q[1] / 3, m[2] + q[2] / 3], [0, 0, 0]);
    const len = Math.hypot(...c) || 1;
    return { kind, n: [c[0] / len, c[1] / len, c[2] / len], p };
  };
  return waist.flatMap((w, i) => {
    const next = waist[(i + 1) % 4];
    return [face('top', [[0, h, 0], next, w]), face('side', [[0, -h, 0], w, next])];
  });
}

/** A square box of half-width `a` and half-height `b`: the four-sided prism. */
const box = (a: number, b: number) => prism(4, a * Math.SQRT2, b);

/** The class's polyhedron in its own frame, sized by `s` (the head's size), or null for an outline. */
function shapeOf(klass: FigureClass, s: number) {
  if (klass === 'tank' || klass === 'skimmer') return box(s / 2, s / 2);
  if (klass === 'bike') return box(s * 0.3, s * 0.15);
  if (klass === 'walker') return prism(6, s * 0.6, s * 0.25);
  if (klass === 'object') return octahedron(s);
  return null;
}

/**
 * The head's faces in world space around `centre`, turned to `headingDeg`
 * (0 looks along +x, 90 along +z: the battlefield's convention), or null for
 * the living and the amorphous.
 */
export function headFaces(klass: FigureClass, centre: Vec3, size: number, headingDeg: number): HeadFace[] | null {
  const shape = shapeOf(klass, size);
  if (!shape) return null;
  const h = (headingDeg * Math.PI) / 180;
  const ch = Math.cos(h);
  const sh = Math.sin(h);
  const turn = ([x, y, z]: Local): Vec3 => ({ x: x * ch - z * sh, y, z: x * sh + z * ch });
  const at = (l: Local): Vec3 => {
    const t = turn(l);
    return { x: centre.x + t.x, y: centre.y + t.y, z: centre.z + t.z };
  };
  return shape.map((f) => ({ kind: f.kind, normal: turn(f.n), points: f.p.map(at) }));
}

/** One head face as the screen draws it: a closed path, and how far away it is (larger is further). */
export interface ShownFace {
  kind: HeadFaceKind;
  d: string;
  depth: number;
}

/**
 * The faces turned toward the viewer, projected, the farthest first so the
 * nearer ones paint over it (a convex head needs no more than that). `place`
 * puts a point on the screen: the map's projection, or a marker's own frame
 * (battlefieldTokens/markerFrame) - facing is asked of the camera either way.
 */
export function visibleHead(
  faces: readonly HeadFace[],
  camera: Camera,
  view: Viewport,
  place: (p: Vec3) => ScreenPoint = (p) => project(p, camera, view),
): ShownFace[] {
  const sight = sightTo(camera, view);
  return (
    faces
      // The shared test, epsilon and all: seen straight down, a side face edge-on must not leak as a sliver.
      .filter((f) => isFrontFacing(f.normal, sight(f.points[0])))
      .map((f) => {
        const pts = f.points.map(place);
        return { kind: f.kind, d: closedPath(pts), depth: pts.reduce((sum, p) => sum + p.depth, 0) / pts.length };
      })
      .sort((a, b) => b.depth - a.depth)
  );
}
